import { DataTable } from "@bcc/components/data-table";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { sampleRoasters } from "@bcc/sampleData/roaster-list.sample-data";
import type { DataTableRow } from "@client-shared";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import { ArrowRight } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { RoasterListItem } from "./roaster-list.types";
import { setImpersonateButtonTestId, setRoasterListTestId } from "./roaster-list.utils";

const ROASTER_STATUS = {
  active: "active",
  inactive: "inactive",
  pending: "pending",
} as const;

export interface RoasterListProps {
  /**
   * List of roasters to display
   * @default sampleRoasters
   */
  readonly roasters?: DataTableRow<RoasterListItem>[];
  /**
   * Callback when a roaster is selected for impersonation
   */
  readonly onImpersonate?: (roasterId: string) => void;
  /**
   * Whether data is loading
   */
  readonly isLoading?: boolean;
}

function RoasterNameCell({ getValue }: CellContext<DataTableRow<RoasterListItem>, unknown>) {
  return <span className="font-medium">{String(getValue())}</span>;
}

function RoasterEmailCell({ getValue }: CellContext<DataTableRow<RoasterListItem>, unknown>) {
  return <span className="text-muted-foreground">{String(getValue())}</span>;
}

function AccountTypeCell({ row }: CellContext<DataTableRow<RoasterListItem>, unknown>) {
  return (
    <Badge variant="secondary" className="capitalize">
      {row.original.accountType}
    </Badge>
  );
}

function getStatusBadgeVariant(status: RoasterListItem["status"]): "default" | "outline" | "secondary" {
  if (status === ROASTER_STATUS.active) return "default";
  if (status === ROASTER_STATUS.pending) return "outline";
  return "secondary";
}

function StatusCell({ row }: CellContext<DataTableRow<RoasterListItem>, unknown>) {
  const { status } = row.original;
  return (
    <Badge variant={getStatusBadgeVariant(status)} className="capitalize">
      {status}
    </Badge>
  );
}

function ActionsCell({
  row,
  handleImpersonate,
}: CellContext<DataTableRow<RoasterListItem>, unknown> & {
  handleImpersonate: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end">
      <Button
        variant="ghost"
        size="icon"
        data-testid={setImpersonateButtonTestId(row.original.id)}
        onClick={() => handleImpersonate(row.original.id)}
        disabled={row.original.status !== ROASTER_STATUS.active}
      >
        <ArrowRight className="size-4" />
        <span className="sr-only">
          {t("admin.roasterList.impersonateAriaLabel", {
            roasterName: row.original.roasterName,
          })}
        </span>
      </Button>
    </div>
  );
}

function createActionsCellRenderer(handleImpersonate: (id: string) => void) {
  function BoundActionsCell(props: CellContext<DataTableRow<RoasterListItem>, unknown>) {
    return <ActionsCell {...props} handleImpersonate={handleImpersonate} />;
  }
  return BoundActionsCell;
}

/**
 * RoasterList Component
 *
 * Displays a table of roasters for admin impersonation.
 * Includes search, filters, pagination, and action button to impersonate.
 */
export function RoasterList({ roasters = sampleRoasters, onImpersonate, isLoading = false }: RoasterListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleImpersonate = React.useCallback(
    (roasterId: string) => {
      if (onImpersonate) {
        onImpersonate(roasterId);
      } else {
        navigate(ROUTE_PATHS.DASHBOARD);
      }
    },
    [onImpersonate, navigate]
  );

  const columns = React.useMemo<ColumnDef<DataTableRow<RoasterListItem>>[]>(
    () => [
      {
        accessorKey: "roasterName",
        header: t("admin.roasterList.columns.roasterName"),
        cell: RoasterNameCell,
      },
      {
        accessorKey: "roasterEmail",
        header: t("admin.roasterList.columns.vendorId"),
        cell: RoasterEmailCell,
      },
      {
        accessorKey: "accountType",
        header: t("admin.roasterList.columns.accountType"),
        cell: AccountTypeCell,
      },
      {
        accessorKey: "status",
        header: t("admin.roasterList.columns.status"),
        cell: StatusCell,
      },
      {
        id: "actions",
        header: "",
        cell: createActionsCellRenderer(handleImpersonate),
      },
    ],
    [t, handleImpersonate]
  );

  return (
    <div data-testid={setRoasterListTestId("container")} className="space-y-4">
      <DataTable
        data={roasters}
        columns={columns}
        searchable
        searchPlaceholder={t("admin.roasterList.searchPlaceholder")}
        filters={[
          {
            label: t("admin.roasterList.filters.beanz"),
            column: "accountType",
            value: "beanz",
          },
          {
            label: t("admin.roasterList.filters.shipstation"),
            column: "accountType",
            value: "shipstation",
          },
          {
            label: t("admin.roasterList.filters.active"),
            column: "status",
            value: ROASTER_STATUS.active,
          },
          {
            label: t("admin.roasterList.filters.inactive"),
            column: "status",
            value: ROASTER_STATUS.inactive,
          },
        ]}
        isLoading={isLoading}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
}
